import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import BrandMark from "../components/BrandMark";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useI18n } from "../i18n/LanguageContext";

/**
 * Public referral landing.
 *
 * URL:  /referral/:code (e.g., /referral/BR-ROSSI-2026)
 * URL param: ?lang=xx  (forces IT/EN/NO/DE; falls back to navigator.language)
 *
 * Renders:
 *   - "You've been introduced by <Name>" branding (in the visitor's language)
 *   - Contact form (name, email, phone, message) — anonymous, no auth
 *   - After submit, "thank you" state
 *
 * Language auto-detection: this landing is shared by introducers across
 * Europe, so we honour ?lang=xx first (introducer-forced), then fall back to
 * the visitor's browser language (navigator.language) so a German recipient
 * sees the page in DE without any manual action.
 */
export default function PublicReferralPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const { t, locale, setLocale } = useI18n();
  const [state, setState] = useState({ status: "loading", intro: null });
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  // Auto-select language on mount (public visitor, unauthenticated).
  //   1. ?lang=xx query param wins (introducer-forced)
  //   2. Otherwise honour navigator.language if it maps to a supported locale
  //   3. Otherwise leave the default (from localStorage / IT)
  useEffect(() => {
    const urlLang = (searchParams.get("lang") || "").toLowerCase();
    const supported = ["it", "en", "no", "de"];
    let candidate = null;
    if (urlLang && supported.includes(urlLang)) {
      candidate = urlLang;
    } else if (typeof navigator !== "undefined" && navigator.language) {
      const browser = String(navigator.language).slice(0, 2).toLowerCase();
      // "nb" (Norwegian Bokmål) & "nn" (Nynorsk) → map to "no"
      const norm = browser === "nb" || browser === "nn" ? "no" : browser;
      if (supported.includes(norm)) candidate = norm;
    }
    if (candidate && candidate !== locale) {
      setLocale(candidate);
    }
    // Only run once at mount — the visitor may then use the LanguageSwitcher.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/referral/${encodeURIComponent(code)}`
        );
        if (!cancelled) setState({ status: "ready", intro: data });
      } catch (err) {
        if (!cancelled) setState({ status: "invalid", intro: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [BACKEND_URL, code]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError(t("referral.err_required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/referral/leads`, {
        code: state.intro.code,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
        locale: locale || "it",
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || t("referral.err_generic")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen br-aurora flex items-center justify-center px-6 py-12 relative"
      data-testid="referral-page"
    >
      <div className="absolute inset-0 br-grain" />

      <div className="absolute top-6 right-6 sm:top-8 sm:right-8 z-10">
        <LanguageSwitcher variant="compact" />
      </div>

      <div className="relative max-w-[560px] w-full br-fade-up">
        <div className="flex items-center gap-4 mb-10">
          <BrandMark size={48} />
          <div>
            <div className="font-serif-display italic text-[1.8rem] text-[color:var(--br-parchment)] leading-none">
              Borealis <span className="text-[color:var(--br-bronze-soft)]">Reserve</span>
            </div>
            <div className="font-mono-fin text-[0.62rem] tracking-[0.36em] uppercase text-[color:var(--br-bronze)] mt-2">
              {t("referral.brand_tagline")}
            </div>
          </div>
        </div>

        <div className="br-card br-aurora-ribbon relative px-9 py-10" data-testid="referral-card">
          {state.status === "loading" && (
            <div className="font-mono-fin text-xs tracking-[0.32em] uppercase text-[color:var(--br-text-dim)]">
              {t("referral.checking")}
            </div>
          )}

          {state.status === "invalid" && (
            <div data-testid="referral-invalid">
              <div className="br-overline" style={{ color: "var(--br-red)" }}>
                {t("referral.invalid_overline")}
              </div>
              <h1 className="font-serif-display text-[2rem] mt-4">
                {t("referral.invalid_title")}
              </h1>
              <p className="text-[0.95rem] mt-3 text-[color:var(--br-text)] leading-relaxed">
                {t("referral.invalid_body")}
              </p>
              <Link
                to="/login"
                className="br-btn-outline mt-6 inline-block"
                data-testid="referral-back-login"
              >
                {t("referral.back_to_login")}
              </Link>
            </div>
          )}

          {state.status === "ready" && !submitted && (
            <>
              <div className="br-overline">{t("referral.overline")}</div>
              <h1 className="font-serif-display text-[2.2rem] mt-4 leading-[1.1]">
                {t("referral.heading_a")}{" "}
                <em>{state.intro.name}</em>{" "}
                {t("referral.heading_b")}
              </h1>
              <p className="text-[0.95rem] text-[color:var(--br-text)] mt-3 leading-relaxed">
                {t("referral.intro")}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-6" data-testid="referral-form">
                <label className="block">
                  <span className="br-overline block mb-3">{t("referral.field_name")}</span>
                  <input
                    className="br-input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    autoComplete="name"
                    data-testid="referral-name-input"
                  />
                </label>
                <label className="block">
                  <span className="br-overline block mb-3">{t("referral.field_email")}</span>
                  <input
                    type="email"
                    className="br-input"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    autoComplete="email"
                    data-testid="referral-email-input"
                  />
                </label>
                <label className="block">
                  <span className="br-overline block mb-3">{t("referral.field_phone")}</span>
                  <input
                    className="br-input"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    autoComplete="tel"
                    data-testid="referral-phone-input"
                  />
                </label>
                <label className="block">
                  <span className="br-overline block mb-3">{t("referral.field_message")}</span>
                  <textarea
                    className="br-input"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    data-testid="referral-message-input"
                  />
                </label>

                {error && (
                  <div
                    className="text-[0.85rem] py-3 px-4 border"
                    style={{
                      borderColor: "var(--br-red)",
                      color: "var(--br-red)",
                      background: "rgba(179,107,107,0.06)",
                    }}
                    data-testid="referral-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="br-btn-primary w-full"
                  disabled={submitting}
                  data-testid="referral-submit-button"
                >
                  {submitting ? t("referral.submitting") : t("referral.cta")}
                </button>
                <p className="text-[0.72rem] text-[color:var(--br-text-dim)] leading-relaxed">
                  {t("referral.privacy_note")}
                </p>
              </form>
            </>
          )}

          {state.status === "ready" && submitted && (
            <div data-testid="referral-thanks">
              <div className="br-overline">{t("referral.thanks_overline")}</div>
              <h2 className="font-serif-display text-[1.9rem] mt-4">
                {t("referral.thanks_title")}
              </h2>
              <p className="mt-4 text-[0.95rem] text-[color:var(--br-text)] leading-relaxed">
                {t("referral.thanks_body")}
              </p>
              <Link
                to="/login"
                className="br-btn-outline mt-6 inline-block"
                data-testid="referral-thanks-back"
              >
                {t("referral.back_to_login")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
